import { cn } from '@/lib/utils';
import { Bot, User } from 'lucide-react';
import { motion } from 'framer-motion';
import type { ChatMessage as ChatMessageType } from '@/shared/workflowAITypes';

interface ChatMessageProps {
  message: ChatMessageType;
  children?: React.ReactNode; // Interactive blocks (questions, template carousel, etc.)
}

export function ChatMessage({ message, children }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={cn('flex gap-3 w-full', isUser ? 'flex-row-reverse' : 'flex-row')}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm',
          isUser
            ? 'bg-gradient-to-br from-workfinity-500 to-workfinity-700'
            : 'bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-200/60',
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-white" />
        ) : (
          <Bot className="h-4 w-4 text-workfinity-600" />
        )}
      </div>

      {/* Bubble */}
      <div
        className={cn(
          'max-w-[85%] sm:max-w-[80%] min-w-0 flex flex-col space-y-1.5',
          isUser ? 'items-end' : 'items-start',
        )}
      >
        {/* Text bubble */}
        <div
          className={cn(
            'rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm break-words [overflow-wrap:anywhere] max-w-full overflow-hidden',
            isUser
              ? 'bg-gradient-to-br from-workfinity-500 to-workfinity-700 text-white rounded-br-md'
              : 'backdrop-blur-sm bg-white/80 border border-slate-200/60 text-slate-800 rounded-bl-md',
          )}
        >
          {/* Render markdown-like bold */}
          <p
            className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-w-full"
            dangerouslySetInnerHTML={{
              __html: message.text
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>'),
            }}
          />
        </div>

        {/* Timestamp */}
        <p
          className={cn(
            'text-[10px] text-slate-400 px-1',
            isUser ? 'text-right' : 'text-left',
          )}
        >
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>

        {/* Interactive content block (only for AI messages) */}
        {!isUser && children && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            className="mt-2 w-full max-w-full overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
